import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Package, MapPin, CheckCircle2, Clock, Send, Copy, Check } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const STATUS_LABEL = {
  pending: 'Awaiting pickup',
  assigned: 'Driver assigned',
  in_progress: 'Out for delivery',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const MyMultiDrops = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [copied, setCopied] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const api = (await import('../utils/api')).default;
      const { data } = await api.get('/deliveries/multi-drop/mine');
      setItems(data.items || []);
    } catch {
      toast({ title: 'Failed to load', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSendTracking = async (booking) => {
    setSending(s => ({ ...s, [booking.id]: true }));
    try {
      const api = (await import('../utils/api')).default;
      const { data } = await api.post(`/deliveries/multi-drop/${booking.id}/send-tracking`, { channels: ['sms'] });
      toast({
        title: 'Tracking links sent',
        description: `${data.totalSent} of ${data.results.length} receiver(s) notified by SMS.`,
      });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSending(s => ({ ...s, [booking.id]: false })); }
  };

  const handleCopy = async (token) => {
    const url = `${window.location.origin}/track/package/${token}`;
    await navigator.clipboard.writeText(url);
    setCopied(token);
    toast({ title: 'Tracking link copied' });
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-10">
      <div className="max-w-4xl mx-auto px-4 space-y-5" data-testid="my-multi-drops-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Package className="h-6 w-6 text-pink-600" /> My Multi-Drop Deliveries</h1>
            <p className="text-sm text-gray-500">Track all your sent packages and notify receivers in one tap.</p>
          </div>
          <Button onClick={() => navigate('/multi-drop')} className="bg-pink-600 hover:bg-pink-700">
            New booking
          </Button>
        </div>

        {loading && <p className="text-gray-500">Loading...</p>}

        {!loading && items.length === 0 && (
          <Card className="p-8 text-center text-gray-500">
            <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No multi-drop deliveries yet.</p>
          </Card>
        )}

        <div className="space-y-3">
          {items.map(b => (
            <Card key={b.id} className="p-4">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">From {b.pickupLocation}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 font-medium">
                      {STATUS_LABEL[b.status] || b.status}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                      {b.completedStops || 0}/{b.totalStops} delivered
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">K{(b.estimatedFare || 0).toFixed(2)} fare {b.codTotal > 0 && `· K${b.codTotal.toFixed(2)} COD`}</p>
                  <p className="text-xs text-gray-400 mt-1">Booked {new Date(b.createdAt).toLocaleString()}</p>

                  <div className="mt-2 space-y-1">
                    {(b.stops || []).slice(0, 3).map(s => (
                      <div key={s.order} className="flex items-center gap-2 text-xs text-gray-600">
                        {s.status === 'delivered'
                          ? <CheckCircle2 className="h-3 w-3 text-green-600 flex-shrink-0" />
                          : <Clock className="h-3 w-3 text-gray-400 flex-shrink-0" />}
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{s.receiverName} · {s.location}</span>
                      </div>
                    ))}
                    {b.stops && b.stops.length > 3 && (
                      <p className="text-xs text-gray-400">+{b.stops.length - 3} more</p>
                    )}
                  </div>

                  {b.customersNotifiedAt && (
                    <p className="text-xs text-emerald-600 mt-2">✓ Receivers notified {new Date(b.customersNotifiedAt).toLocaleString()}</p>
                  )}
                </div>

                <div className="flex flex-col gap-2 w-44">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleCopy(b.trackingToken)}
                    data-testid={`copy-track-${b.id}`}
                  >
                    {copied === b.trackingToken ? <Check className="h-3.5 w-3.5 mr-1 text-green-600" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                    Copy link
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/track/package/${b.trackingToken}`)}
                  >
                    View tracking
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    disabled={sending[b.id] || !!b.customersNotifiedAt}
                    onClick={() => handleSendTracking(b)}
                    data-testid={`send-tracking-${b.id}`}
                    title={b.customersNotifiedAt ? 'Already notified' : 'Send tracking link to all receivers via SMS'}
                  >
                    <Send className="h-3.5 w-3.5 mr-1" />
                    {sending[b.id] ? 'Sending...' : b.customersNotifiedAt ? 'Notified' : 'Send to receivers'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MyMultiDrops;
